import React, { useState, useEffect, useMemo } from 'react';
import { PlusOutlined, FilterOutlined, HolderOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import StatusBadge from '../../components/StatusBadge';
import type { BadgeStatus } from '../../components/StatusBadge';
import PageWrapper from '../../components/PageWrapper';
import FilterBadges from '../../components/FilterBadges';
import DataTable, { DragHandle, type SortConfig, type SortOrder } from '../../components/DataTable';
import { useInfiniteContents } from '../../hooks/useInfiniteContents';
import { useRanking } from '../../hooks/useRanking';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import type { DragEndEvent } from '@dnd-kit/core';
import { formatDateTurkish } from '../../helpers/mixins';
import { getRankUpdatesAfterReorder } from '../../helpers/rankUtils';
import { useQueryParams } from '../../helpers/queryUtils';
import BlogModal from '../../modals/blog/BlogModal';
import BlogFilterModal from '../../modals/blog/BlogFilterModal';
import UnsavedChangesModal from '../../components/UnsavedChangesModal';

interface FilterParams {
  status?: 'Active' | 'Passive';
  startDate?: string;
  endDate?: string;
}

const Blog: React.FC = () => {
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [modalStatus, setModalStatus] = useState<'create' | string>();
  const [filters, setFilters] = useState<FilterParams>({});
  const [searchText, setSearchText] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingRankingData, setPendingRankingData] = useState<{ id: string; rank: number }[]>([]);
  const [optimisticBlogList, setOptimisticBlogList] = useState<any[]>([]);
  const [currentItems, setCurrentItems] = useState<{ id: string; rank: number }[]>([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'Status', order: 'default' });

  const queryParams = useQueryParams(
    {
      type: 'Blog' as const,
      pageSize: 10,
    },
    sortConfig,
    {
      ...filters,
      ...(searchText && { name: searchText }),
    },
    'Rank'
  );

  const { data: infiniteData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } = useInfiniteContents(queryParams);

  const originalBlogList = useMemo(() => infiniteData?.pages.flatMap((page) => page.result || []) || [], [infiniteData?.pages]);

  const blogList = hasUnsavedChanges ? optimisticBlogList : originalBlogList;
  const rankingMutation = useRanking();

  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).length > 0 || searchText.trim().length > 0;
  }, [filters, searchText]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      setOptimisticBlogList(originalBlogList);
    }
  }, [originalBlogList, hasUnsavedChanges]);

  useEffect(() => {
    setCurrentItems(blogList.map((item) => ({ id: item.id, rank: item.rank })));
  }, [blogList]);

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (sortConfig.order !== 'default') {
      return;
    }

    if (event.active && event.over) {
      const currentList = hasUnsavedChanges ? optimisticBlogList : originalBlogList;
      const activeIndex = currentList.findIndex((item) => item.id === event.active.id);
      const overIndex = currentList.findIndex((item) => item.id === event.over?.id);

      if (activeIndex === -1 || overIndex === -1) return;

      const updatedData = [...currentList];
      const [draggedItem] = updatedData.splice(activeIndex, 1);
      updatedData.splice(overIndex, 0, draggedItem);

      setOptimisticBlogList(updatedData);
      setHasUnsavedChanges(true);

      const updatedItems = getRankUpdatesAfterReorder(updatedData);
      setPendingRankingData(updatedItems);
    }
  };

  const handleFilters = () => {
    setOpenFilterModal(true);
  };

  const handleCloseFilterModal = () => {
    setOpenFilterModal(false);
  };

  const handleApplyFilter = (newFilters: FilterParams) => {
    setFilters(newFilters);
    setSortConfig({ column: 'Status', order: 'default' });
  };

  const handleRemoveFilter = (filterKey: keyof FilterParams) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);

    setSortConfig({ column: 'Status', order: 'default' });
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchText('');

    setSortConfig({ column: 'Status', order: 'default' });
  };

  const handleSortChange = (newSortConfig: SortConfig) => {
    if (newSortConfig.column === 'Status') {
      setSortConfig(newSortConfig);
    } else {
      setSortConfig({ column: 'Status', order: 'default' });
    }
  };

  useEffect(() => {
    setSortConfig({ column: 'Status', order: 'default' });
  }, [searchText]);

  const handleCloseCreateModal = () => {
    setModalStatus(undefined);
  };

  const handleCancelCreateModal = () => {
    setModalStatus(undefined);
  };

  const handleSaveRanking = async () => {
    if (pendingRankingData.length > 0) {
      try {
        await rankingMutation.mutateAsync(pendingRankingData);

        const updatedOptimisticList = optimisticBlogList.map((item) => {
          const rankUpdate = pendingRankingData.find((update) => update.id === item.id);
          return rankUpdate ? { ...item, rank: rankUpdate.rank } : item;
        });

        setOptimisticBlogList(updatedOptimisticList);

        setPendingRankingData([]);
        setHasUnsavedChanges(false);
        setSortConfig({ column: 'Status', order: 'default' });
        refetch();
      } catch (error) {
        console.error('Failed to update ranking:', error);
      }
    }
  };

  const handleCancelRanking = () => {
    setPendingRankingData([]);
    setHasUnsavedChanges(false);
    setOptimisticBlogList(originalBlogList);
  };

  // Unsaved changes hook
  const {
    showUnsavedModal,
    handleModalClose,
    handleDiscard,
    handleSaveChanges,
    isLoading: isSavingChanges,
  } = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: handleSaveRanking,
    onDiscard: handleCancelRanking,
  });

  const tableColumns = [
    {
      title: 'Order',
      key: 'rank',
      width: '10%',
      render: (_: number, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {sortConfig.order === 'default' && (
            <DragHandle>
              <span style={{ color: '#a4a7ae', fontSize: '16px' }}>
                <HolderOutlined />
              </span>
            </DragHandle>
          )}
          <span>{record.rank}</span>
        </div>
      ),
    },
    {
      title: 'Blog Title',
      key: 'name',
      width: '50%',
      render: (name: string | null, record: any) => (
        <span
          style={{
            color: hasUnsavedChanges ? 'var(--color-text-disabled)' : 'var(--color-primary)',
            fontWeight: 600,
            cursor: hasUnsavedChanges ? 'not-allowed' : 'pointer',
          }}
          onClick={hasUnsavedChanges ? undefined : () => setModalStatus(record.id)}
        >
          {name || 'Untitled Blog'}
        </span>
      ),
    },

    {
      title: 'Status',
      key: 'status',
      width: '20%',
      sortable: true,
      sortKey: 'Status',
      render: (status: BadgeStatus) => <StatusBadge status={status} />,
    },
    {
      title: '',
      key: 'detail',
      width: '20%',
      render: (_: any, record: any) => (
        <span
          style={{
            color: hasUnsavedChanges ? 'var(--color-text-disabled)' : 'var(--color-primary)',
            cursor: hasUnsavedChanges ? 'not-allowed' : 'pointer',
          }}
          onClick={hasUnsavedChanges ? undefined : () => setModalStatus(record.id)}
        >
          <EditOutlined />
        </span>
      ),
    },
  ];

  return (
    <PageWrapper
      title="Blogs"
      search={!hasUnsavedChanges}
      onSearch={setSearchText}
      actionButtons={(() => {
        const buttons: Array<{
          title: string;
          icon: React.ReactNode;
          permission: boolean;
          theme: 'primary' | 'secondary' | 'danger';
          onClick: () => void;
          disabled?: boolean;
          hasActiveFilters?: boolean;
        }> = [];

        if (hasUnsavedChanges) {
          buttons.push(
            {
              title: 'Cancel',
              icon: <></>,
              permission: true,
              theme: 'secondary',
              onClick: handleCancelRanking,
              disabled: rankingMutation.isPending,
            },
            {
              title: 'Save',
              icon: <SaveOutlined />,
              permission: true,
              theme: 'primary',
              onClick: handleSaveRanking,
              disabled: rankingMutation.isPending,
            }
          );
        } else {
          buttons.push(
            {
              title: 'Filters',
              icon: <FilterOutlined />,
              permission: true,
              theme: 'secondary',
              onClick: handleFilters,
              disabled: false,
              hasActiveFilters: hasActiveFilters,
            },
            {
              title: 'Create Blog',
              icon: <PlusOutlined />,
              permission: true,
              theme: 'primary',
              onClick: () => setModalStatus('create'),
              disabled: false,
            }
          );
        }

        return buttons;
      })()}
    >
      <FilterBadges
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
        onClearAllFilters={handleClearAllFilters}
      />

      <DataTable
        data={blogList}
        children={tableColumns}
        loading={isLoading}
        rowKey="id"
        isDraggable={sortConfig.order === 'default'}
        onDragEnd={handleDragEnd}
        language="tr"
        onLoadMore={handleLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollThreshold={150}
        minHeight={400}
        currentSort={sortConfig}
        onSortChange={handleSortChange}
      />

      {openFilterModal && (
        <BlogFilterModal
          onClose={handleCloseFilterModal}
          onFilter={handleApplyFilter}
          initialValues={filters}
        />
      )}

      {modalStatus && (
        <BlogModal
          blogId={modalStatus}
          onClose={handleCloseCreateModal}
          onCancel={handleCancelCreateModal}
          currentItems={currentItems}
        />
      )}

      {showUnsavedModal && (
        <UnsavedChangesModal
          onClose={handleModalClose}
          onDiscard={handleDiscard}
          onSaveChanges={handleSaveChanges}
          loading={isSavingChanges}
        />
      )}
    </PageWrapper>
  );
};

export default Blog;
